import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import reversalsService from '../../services/reversals.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import type { ReversalRequest, ReversalStatus } from '../../types/finance';

const STATUS_TONE: Record<ReversalStatus, BadgeTone> = {
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
};

/** Action-column content for a non-pending reversal: the rejection reason if
 *  one exists, else an em-dash. Extracted so the action cell stays a single
 *  ternary (pending → buttons, else → this) rather than a nested one. */
function SettledReasonCell({ reason }: Readonly<{ reason: string | null }>): JSX.Element {
  return reason ? (
    <span className="text-xs" style={{ color: colors.muted }}>{reason}</span>
  ) : (
    <span className="text-xs" style={{ color: colors.mutedSoft }}>—</span>
  );
}

interface ReversalActionProps {
  reversal: ReversalRequest;
  approveId: string | null;
  approveCode: string;
  actionId: string | null;
  onStartApprove: (id: string) => void;
  onApproveCodeChange: (code: string) => void;
  onConfirmApprove: (r: ReversalRequest) => void;
  onCancelApprove: () => void;
  onReject: (r: ReversalRequest) => void;
}

/** Action cell for one reversal row: Approve/Reject, or — once Approve is
 *  clicked — an inline step-up code field (money-moving action requires a
 *  fresh TOTP code from the approver). Extracted to keep the table map
 *  render flat (Sonar S3776). */
function renderReversalAction(p: Readonly<ReversalActionProps>): JSX.Element {
  const { reversal: r } = p;
  if (r.status !== 'pending') {
    return <SettledReasonCell reason={r.rejection_reason} />;
  }
  if (p.approveId === r.id) {
    return (
      <span className="inline-flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Authentication code"
          value={p.approveCode}
          onChange={(e) => p.onApproveCodeChange(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder="6-digit code"
          className="px-2 py-1.5 rounded-md text-xs font-mono tracking-widest w-28"
          style={{ border: `1px solid ${colors.border}` }}
        />
        <button
          type="button"
          onClick={() => p.onConfirmApprove(r)}
          disabled={p.actionId !== null || p.approveCode.trim().length < 6}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          {p.actionId === r.id ? '…' : 'Confirm'}
        </button>
        <button
          type="button"
          onClick={p.onCancelApprove}
          disabled={p.actionId !== null}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex gap-2">
      <button
        type="button"
        onClick={() => p.onStartApprove(r.id)}
        disabled={p.actionId !== null}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{ background: gradients.gold, color: colors.navy }}
      >
        Approve
      </button>
      <button
        type="button"
        onClick={() => p.onReject(r)}
        disabled={p.actionId !== null}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
      >
        Reject
      </button>
    </span>
  );
}

type StatusFilter = 'pending' | 'all';

export default function ManagerReversals(): JSX.Element {
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [reversals, setReversals] = useState<ReversalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ReversalRequest | null>(null);
  // Row awaiting a step-up code before its approval commits, + the code.
  const [approveId, setApproveId] = useState<string | null>(null);
  const [approveCode, setApproveCode] = useState('');
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reversalsService.list({
        status: filter === 'pending' ? ['pending'] : undefined,
        size: 100,
      });
      setReversals(res.items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load reversals.'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const startApprove = (id: string): void => {
    setError(null);
    setApproveCode('');
    setApproveId(id);
  };

  const cancelApprove = (): void => {
    setApproveId(null);
    setApproveCode('');
  };

  const confirmApprove = async (r: ReversalRequest): Promise<void> => {
    setActionId(r.id);
    setError(null);
    try {
      await reversalsService.approve(r.id, r.version, approveCode.trim());
      setApproveId(null);
      setApproveCode('');
      setFlash(`Reversal approved — ${formatMoney(r.amount, r.currency_code)} credited back.`);
      globalThis.setTimeout(() => setFlash(null), 6000);
      await reload();
    } catch (e: unknown) {
      setError(approveMfaErrorMessage(e));
    } finally {
      setActionId(null);
    }
  };

  let body: JSX.Element;
  if (loading) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (reversals.length === 0) {
    body = (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        {filter === 'pending'
          ? 'No reversals awaiting your approval.'
          : 'No reversals to display.'}
      </div>
    );
  } else {
    body = (
      <div
        className="overflow-hidden rounded-2xl"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">Reference</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-left font-medium px-4 py-3">Reason</th>
              <th className="text-left font-medium px-4 py-3">Proposed by</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-right font-medium px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {reversals.map((r, idx) => (
              <tr
                key={r.id}
                style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
              >
                <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                  {r.reference_number ?? '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                  {formatMoney(r.amount, r.currency_code)}
                </td>
                <td className="px-4 py-3 max-w-xs" style={{ color: colors.inkSoft }}>
                  <span className="line-clamp-2">{r.reason}</span>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                  <div>{r.proposed_by_email ?? '—'}</div>
                  <div>{formatDateTime(r.created_at)}</div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge tone={STATUS_TONE[r.status]}>{r.status}</StatusBadge>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {renderReversalAction({
                    reversal: r,
                    approveId,
                    approveCode,
                    actionId,
                    onStartApprove: startApprove,
                    onApproveCodeChange: setApproveCode,
                    onConfirmApprove: confirmApprove,
                    onCancelApprove: cancelApprove,
                    onReject: setRejectTarget,
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Reversal approvals"
        subtitle="Reverse an approved withdrawal made in error. The ledger is never edited, so approving posts a linked credit that returns the money to the client and leaves a full audit trail."
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="all">All</option>
            <option value="pending">Pending only</option>
          </select>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {body}

      {rejectTarget && (
        <RejectModal
          target={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onSubmitted={(msg) => {
            setRejectTarget(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 5000);
            reload();
          }}
        />
      )}
    </>
  );
}

interface RejectProps {
  target: ReversalRequest;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

function RejectModal({ target, onClose, onSubmitted }: Readonly<RejectProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const trimmed = reason.trim();
  const valid = trimmed.length >= 5 && trimmed.length <= 1000;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await reversalsService.reject(target.id, target.version, trimmed);
      onSubmitted(`Rejected reversal of ${formatMoney(target.amount, target.currency_code)}.`);
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not reject.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Reject reversal
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Reason is required and stored in the audit log.
      </p>
      <div
        className="mb-4 p-3 rounded-lg text-xs"
        style={{ background: colors.surfaceMuted, color: colors.textPrimary }}
      >
        <div className="font-semibold">{formatMoney(target.amount, target.currency_code)}</div>
        <div style={{ color: colors.textMuted }}>
          Ref: {target.reference_number ?? '—'} · {target.reason}
        </div>
      </div>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        maxLength={1000}
        placeholder="e.g. Reversal not supported under current contract terms."
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
        {trimmed.length}/1000 · minimum 5 characters
      </div>

      {err && (
        <div
          className="mt-3 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !valid}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy || !valid ? colors.disabledBg : colors.danger,
            color: busy || !valid ? colors.disabledText : '#FFFFFF',
          }}
        >
          {busy ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </Modal>
  );
}
